import type { ComponentType, SVGProps } from 'react';
import {
  // Verein & rechtliche Angaben
  BuildingOffice2Icon,
  PhotoIcon,
  IdentificationIcon,
  ScaleIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  // Person & Mitgliedschaft
  UserIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  UserGroupIcon,
  UserPlusIcon,
  UserMinusIcon,
  CalendarDaysIcon,
  CakeIcon,
  // Kontakt & Adresse
  EnvelopeIcon,
  PhoneIcon,
  DevicePhoneMobileIcon,
  MapPinIcon,
  MapIcon,
  HomeIcon,
  BuildingLibraryIcon,
  LinkIcon,
  // Datum, Zeit & Gültigkeit
  CalendarIcon,
  ClockIcon,
  // Finanzen & Zahlungen
  CurrencyEuroIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  CreditCardIcon,
  ReceiptPercentIcon,
  ShoppingCartIcon,
  ArchiveBoxIcon,
  WalletIcon,
  GiftIcon,
  ChartPieIcon,
  // Dokumente & Dateien
  DocumentIcon,
  PaperClipIcon,
  CloudArrowUpIcon,
  ArrowDownTrayIcon,
  CircleStackIcon,
  EyeIcon,
  SparklesIcon,
  // Veranstaltungen & Buchungen
  TicketIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  XCircleIcon,
  // Hunde
  TagIcon,
  ScissorsIcon,
  CpuChipIcon,
  SwatchIcon,
  HomeModernIcon,
  // Status, Prüfung & Sicherheit
  CheckBadgeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  KeyIcon,
  LockClosedIcon,
  ClipboardDocumentCheckIcon,
  // Suche, Filter & Systemfelder
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  Cog6ToothIcon,
  HashtagIcon,
  ChartBarIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

import SideDogIcon from '../components/icons/SideDogIcon';
import SyringeIcon from '../components/icons/SyringeIcon';
import GenderIcon from '../components/icons/GenderIcon';

export type AttributeIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface AttributeIconEntry {
  /** Icon-Komponente (grundsätzlich Heroicons Outline; Ausnahmen zentral gekapselt). */
  component: AttributeIconComponent;
  /** Beispiel-Label / typische Begriffe für diesen Schlüssel. */
  label: string;
  /** Kommagetrennte Synonyme laut Icon-Matrix. */
  synonyms: string;
  /** Thematische Gruppe (für Doku/Demo). */
  group: AttributeGroup;
}

export type AttributeGroup =
  | 'Verein & Recht'
  | 'Person & Mitgliedschaft'
  | 'Kontakt & Adresse'
  | 'Datum & Zeit'
  | 'Finanzen'
  | 'Dokumente & Dateien'
  | 'Veranstaltungen'
  | 'Hunde'
  | 'Status & Sicherheit'
  | 'Suche, Filter & System';

/**
 * Zentrale semantische Zuordnung: Attribut-Schlüssel -> Icon.
 *
 * Grundregel (Issue #9): Derselbe fachliche Begriff verwendet überall dasselbe
 * Icon. Alle Synonyme einer Zeile teilen sich einen Schlüssel. Unbekannte
 * Begriffe NICHT spontan mit einem neuen Icon versehen, sondern hier ergänzen.
 *
 * Verbindliche Ausnahmen von Heroicons Outline:
 *  - `dog`        -> gefülltes SideDogIcon (niemals HeartIcon)
 *  - `vaccination` -> Lucide-Style SyringeIcon
 *  - `gender`     -> einheitliches GenderIcon
 */
export const attributeIconMap = {
  // --- Verein & rechtliche Angaben ---
  organization: { component: BuildingOffice2Icon, label: 'Verein', synonyms: 'Verein, Vereinsname, Organisation', group: 'Verein & Recht' },
  logo: { component: PhotoIcon, label: 'Logo', synonyms: 'Vereinslogo, Logo, Foto, Bild', group: 'Verein & Recht' },
  registrationNumber: { component: IdentificationIcon, label: 'Registrierungsnummer', synonyms: 'Registrierungsnummer, Vereinsregister, VR-Nummer', group: 'Verein & Recht' },
  court: { component: ScaleIcon, label: 'Gericht', synonyms: 'Gericht, Amtsgericht', group: 'Verein & Recht' },
  country: { component: GlobeAltIcon, label: 'Land', synonyms: 'Land, Staat', group: 'Verein & Recht' },
  description: { component: DocumentTextIcon, label: 'Beschreibung', synonyms: 'Beschreibung, Bemerkung, Notiz', group: 'Verein & Recht' },

  // --- Person & Mitgliedschaft ---
  name: { component: UserIcon, label: 'Name', synonyms: 'Name, Vorname, Nachname, Ansprechpartner', group: 'Person & Mitgliedschaft' },
  member: { component: UserCircleIcon, label: 'Mitglied', synonyms: 'Mitglied, Mitgliedsdaten, Benutzer', group: 'Person & Mitgliedschaft' },
  memberNumber: { component: IdentificationIcon, label: 'Mitgliedsnummer', synonyms: 'Mitgliedsnummer, Mitglieder-Nr., Nummer, Nr.', group: 'Person & Mitgliedschaft' },
  role: { component: ShieldCheckIcon, label: 'Rolle', synonyms: 'Rolle, Funktion, Berechtigung, Vorstand', group: 'Person & Mitgliedschaft' },
  trainer: { component: AcademicCapIcon, label: 'Trainer', synonyms: 'Trainer, Co-Trainer, Leitung', group: 'Person & Mitgliedschaft' },
  participants: { component: UserGroupIcon, label: 'Teilnehmer', synonyms: 'Teilnehmer, Mitgliederzahl, Personen', group: 'Person & Mitgliedschaft' },
  guest: { component: UserPlusIcon, label: 'Gast', synonyms: 'Gast, Gäste', group: 'Person & Mitgliedschaft' },
  memberSince: { component: CalendarDaysIcon, label: 'Mitglied seit', synonyms: 'Eintritt, Mitglied seit', group: 'Person & Mitgliedschaft' },
  memberUntil: { component: UserMinusIcon, label: 'Austritt', synonyms: 'Austritt, deaktiviert seit', group: 'Person & Mitgliedschaft' },
  birthday: { component: CakeIcon, label: 'Geburtsdatum', synonyms: 'Geburtsdatum, Geburtstag', group: 'Person & Mitgliedschaft' },
  gender: { component: GenderIcon, label: 'Geschlecht', synonyms: 'Geschlecht', group: 'Person & Mitgliedschaft' },

  // --- Kontakt & Adresse ---
  email: { component: EnvelopeIcon, label: 'E-Mail', synonyms: 'E-Mail, E-Mail-Adresse', group: 'Kontakt & Adresse' },
  phone: { component: PhoneIcon, label: 'Telefon', synonyms: 'Telefon, Telefonnummer', group: 'Kontakt & Adresse' },
  mobile: { component: DevicePhoneMobileIcon, label: 'Mobil', synonyms: 'Mobil, Handynummer', group: 'Kontakt & Adresse' },
  address: { component: MapPinIcon, label: 'Adresse', synonyms: 'Adresse, vollständige Adresse, Anschrift, PLZ, Postleitzahl', group: 'Kontakt & Adresse' },
  street: { component: MapIcon, label: 'Straße', synonyms: 'Straße', group: 'Kontakt & Adresse' },
  houseNumber: { component: HomeIcon, label: 'Hausnummer', synonyms: 'Hausnummer', group: 'Kontakt & Adresse' },
  city: { component: BuildingLibraryIcon, label: 'Ort', synonyms: 'Ort, Stadt', group: 'Kontakt & Adresse' },
  website: { component: LinkIcon, label: 'Website', synonyms: 'Website, URL, Link', group: 'Kontakt & Adresse' },

  // --- Datum, Zeit & Gültigkeit ---
  date: { component: CalendarDaysIcon, label: 'Datum', synonyms: 'Datum, Beginn, Ende, Termin, erstellt am, geändert am, gültig bis', group: 'Datum & Zeit' },
  year: { component: CalendarIcon, label: 'Jahr', synonyms: 'Jahr, Abrechnungsjahr', group: 'Datum & Zeit' },
  time: { component: ClockIcon, label: 'Uhrzeit', synonyms: 'Uhrzeit, Dauer, Frist, Stornierungsfrist, letzte Aktivität, letzter Login', group: 'Datum & Zeit' },

  // --- Finanzen & Zahlungen ---
  amount: { component: CurrencyEuroIcon, label: 'Betrag', synonyms: 'Betrag, Preis, Kosten, Beitrag, Gebühr', group: 'Finanzen' },
  income: { component: ArrowTrendingUpIcon, label: 'Einnahmen', synonyms: 'Einnahmen, Gutschrift', group: 'Finanzen' },
  expense: { component: ArrowTrendingDownIcon, label: 'Ausgaben', synonyms: 'Ausgaben, Belastung', group: 'Finanzen' },
  balance: { component: BanknotesIcon, label: 'Kontostand', synonyms: 'Kontostand, Bilanz, Saldo, Buchung, Finanzbuchung', group: 'Finanzen' },
  payment: { component: CreditCardIcon, label: 'Zahlung', synonyms: 'Zahlung, Zahlungsart, Bezahlt', group: 'Finanzen' },
  bankAccount: { component: BuildingLibraryIcon, label: 'Konto', synonyms: 'Konto, Bankkonto, IBAN, Girokonto', group: 'Finanzen' },
  cashBox: { component: BanknotesIcon, label: 'Bargeldkasse', synonyms: 'Bargeldkasse, Barkasse, Portemonaie, Kasse', group: 'Finanzen' },
  drinksAccount: { component: ShoppingCartIcon, label: 'Getränkekasse', synonyms: 'Getränkekasse, Verzehrkosten, Kantine', group: 'Finanzen' },
  reserves: { component: ArchiveBoxIcon, label: 'Rücklage', synonyms: 'Rücklage, Rücklagen, Sparkonto', group: 'Finanzen' },
  donation: { component: GiftIcon, label: 'Spende', synonyms: 'Spende, Spenden, Spendenkasse', group: 'Finanzen' },
  assets: { component: WalletIcon, label: 'Vereinsvermögen', synonyms: 'Vereinsvermögen, Gesamtvermögen', group: 'Finanzen' },
  utilization: { component: ChartPieIcon, label: 'Auslastung', synonyms: 'Auslastung, Belegung, Quote', group: 'Finanzen' },
  purpose: { component: DocumentTextIcon, label: 'Verwendungszweck', synonyms: 'Verwendungszweck, Buchungstext', group: 'Finanzen' },
  receipt: { component: ReceiptPercentIcon, label: 'Rechnung', synonyms: 'Rechnung, Beleg, Quittung', group: 'Finanzen' },

  // --- Dokumente & Dateien ---
  document: { component: DocumentTextIcon, label: 'Dokument', synonyms: 'Dokument, Datei, Dateiname, Nachweis', group: 'Dokumente & Dateien' },
  documentType: { component: DocumentIcon, label: 'Dokumenttyp', synonyms: 'Dokumenttyp, Dateityp', group: 'Dokumente & Dateien' },
  attachment: { component: PaperClipIcon, label: 'Anhang', synonyms: 'Anhang, Anhänge', group: 'Dokumente & Dateien' },
  upload: { component: CloudArrowUpIcon, label: 'Upload', synonyms: 'Upload, hochladen', group: 'Dokumente & Dateien' },
  download: { component: ArrowDownTrayIcon, label: 'Download', synonyms: 'Download, herunterladen', group: 'Dokumente & Dateien' },
  fileSize: { component: CircleStackIcon, label: 'Dateigröße', synonyms: 'Dateigröße', group: 'Dokumente & Dateien' },
  preview: { component: EyeIcon, label: 'Vorschau', synonyms: 'Vorschau', group: 'Dokumente & Dateien' },
  link: { component: LinkIcon, label: 'Verknüpfung', synonyms: 'Verknüpfung, verweisende Buchung', group: 'Dokumente & Dateien' },
  aiAnalysis: { component: SparklesIcon, label: 'KI-Analyse', synonyms: 'KI-Analyse', group: 'Dokumente & Dateien' },

  // --- Veranstaltungen & Buchungen ---
  event: { component: CalendarDaysIcon, label: 'Veranstaltung', synonyms: 'Veranstaltung, Event, Termin, Kurs', group: 'Veranstaltungen' },
  eventTitle: { component: TicketIcon, label: 'Titel', synonyms: 'Titel, Veranstaltungsname', group: 'Veranstaltungen' },
  capacity: { component: UsersIcon, label: 'Max. Teilnehmer', synonyms: 'Max. Teilnehmer, Kapazität', group: 'Veranstaltungen' },
  location: { component: MapPinIcon, label: 'Ort', synonyms: 'Ort, Treffpunkt', group: 'Veranstaltungen' },
  booking: { component: TicketIcon, label: 'Buchung', synonyms: 'Buchung, Anmeldung', group: 'Veranstaltungen' },
  waitlist: { component: ClipboardDocumentListIcon, label: 'Warteliste', synonyms: 'Warteliste', group: 'Veranstaltungen' },
  recurrence: { component: ArrowPathIcon, label: 'Wiederholung', synonyms: 'Wiederholung, Serie, Rhythmus', group: 'Veranstaltungen' },
  cancellation: { component: XCircleIcon, label: 'Absage', synonyms: 'Absage, Stornierung', group: 'Veranstaltungen' },

  // --- Hunde ---
  dog: { component: SideDogIcon, label: 'Hund', synonyms: 'Hund, Hundename, Rufname', group: 'Hunde' },
  breedingName: { component: IdentificationIcon, label: 'Zuchtname', synonyms: 'Zuchtname, offizieller Name, Zuchtbuchnummer', group: 'Hunde' },
  owner: { component: UserIcon, label: 'Halter', synonyms: 'Halter, Eigentümer', group: 'Hunde' },
  breed: { component: TagIcon, label: 'Rasse', synonyms: 'Rasse', group: 'Hunde' },
  tassoNumber: { component: IdentificationIcon, label: 'Tasso-Nummer', synonyms: 'Tasso-Nummer, Tasso-Nr., Registriernummer (Hund)', group: 'Hunde' },
  litterDate: { component: CakeIcon, label: 'Wurfdatum', synonyms: 'Wurfdatum, Geburtstag (Hund)', group: 'Hunde' },
  age: { component: CalendarDaysIcon, label: 'Alter', synonyms: 'Alter', group: 'Hunde' },
  neutered: { component: ScissorsIcon, label: 'Kastriert', synonyms: 'Kastriert, Kastration', group: 'Hunde' },
  chipNumber: { component: CpuChipIcon, label: 'Chipnummer', synonyms: 'Chipnummer, Transponder', group: 'Hunde' },
  coatColor: { component: SwatchIcon, label: 'Fellfarbe', synonyms: 'Farbe, Fellfarbe', group: 'Hunde' },
  dogBox: { component: HomeModernIcon, label: 'Hundebox', synonyms: 'Hundebox, Box', group: 'Hunde' },
  qualification: { component: AcademicCapIcon, label: 'Prüfung', synonyms: 'Prüfung, Qualifikation', group: 'Hunde' },
  vaccination: { component: SyringeIcon, label: 'Tollwutimpfung', synonyms: 'Tollwutimpfung, Impfstatus, Geimpft', group: 'Hunde' },
  liability: { component: ShieldCheckIcon, label: 'Haftpflichtversicherung', synonyms: 'Haftpflichtversicherung, Versichert', group: 'Hunde' },

  // --- Status, Prüfung & Sicherheit ---
  status: { component: CheckBadgeIcon, label: 'Status', synonyms: 'Status, Zustand', group: 'Status & Sicherheit' },
  active: { component: CheckCircleIcon, label: 'Aktiv', synonyms: 'Aktiv, gültig, abgeschlossen', group: 'Status & Sicherheit' },
  inactive: { component: XCircleIcon, label: 'Inaktiv', synonyms: 'Inaktiv, ungültig, abgelaufen', group: 'Status & Sicherheit' },
  pending: { component: ClockIcon, label: 'Offen', synonyms: 'Offen, ausstehend, in Prüfung', group: 'Status & Sicherheit' },
  warning: { component: ExclamationTriangleIcon, label: 'Warnung', synonyms: 'Warnung, unvollständig', group: 'Status & Sicherheit' },
  error: { component: ExclamationCircleIcon, label: 'Fehler', synonyms: 'Fehler, abgelehnt', group: 'Status & Sicherheit' },
  password: { component: KeyIcon, label: 'Passwort', synonyms: 'Passwort', group: 'Status & Sicherheit' },
  security: { component: ShieldCheckIcon, label: 'Sicherheit', synonyms: 'Sicherheit, Zugriff, Freigabe', group: 'Status & Sicherheit' },
  privacy: { component: LockClosedIcon, label: 'Datenschutz', synonyms: 'Datenschutz, gesperrt', group: 'Status & Sicherheit' },
  audit: { component: ClipboardDocumentCheckIcon, label: 'Audit', synonyms: 'Audit, Historie, Protokoll', group: 'Status & Sicherheit' },

  // --- Suche, Filter & Systemfelder ---
  search: { component: MagnifyingGlassIcon, label: 'Suche', synonyms: 'Suche', group: 'Suche, Filter & System' },
  filter: { component: FunnelIcon, label: 'Filter', synonyms: 'Filter', group: 'Suche, Filter & System' },
  sort: { component: ArrowsUpDownIcon, label: 'Sortierung', synonyms: 'Sortierung', group: 'Suche, Filter & System' },
  category: { component: TagIcon, label: 'Kategorie', synonyms: 'Kategorie, Typ, Gruppe', group: 'Suche, Filter & System' },
  settings: { component: Cog6ToothIcon, label: 'Einstellungen', synonyms: 'Einstellungen, Konfiguration', group: 'Suche, Filter & System' },
  id: { component: HashtagIcon, label: 'ID', synonyms: 'ID, technische Referenz', group: 'Suche, Filter & System' },
  count: { component: ChartBarIcon, label: 'Anzahl', synonyms: 'Anzahl, Gesamt', group: 'Suche, Filter & System' },
  dashboard: { component: Squares2X2Icon, label: 'Übersicht', synonyms: 'Übersicht, Dashboard, Lagebild', group: 'Suche, Filter & System' },
  refresh: { component: ArrowPathIcon, label: 'Aktualisieren', synonyms: 'Aktualisieren, Neu laden', group: 'Suche, Filter & System' },
} satisfies Record<string, AttributeIconEntry>;

export type AttributeIconKey = keyof typeof attributeIconMap;

/** Reihenfolge der Gruppen für Doku/Demo-Anzeige. */
export const ATTRIBUTE_GROUP_ORDER: AttributeGroup[] = [
  'Verein & Recht',
  'Person & Mitgliedschaft',
  'Kontakt & Adresse',
  'Datum & Zeit',
  'Finanzen',
  'Dokumente & Dateien',
  'Veranstaltungen',
  'Hunde',
  'Status & Sicherheit',
  'Suche, Filter & System',
];

/**
 * Icon-Komponente zu einem Attribut-Schlüssel holen. Gibt `undefined` zurück,
 * wenn der Schlüssel nicht existiert (bewusst kein stiller Fallback auf ein
 * beliebiges Icon – unbekannte Begriffe sollen in der Map ergänzt werden).
 */
export function getAttributeIcon(
  key: AttributeIconKey,
): AttributeIconComponent | undefined {
  return attributeIconMap[key]?.component;
}

export const ATTRIBUTE_ICON_KEYS = Object.keys(attributeIconMap) as AttributeIconKey[];
